import { SubtenantDto } from '@lmt/shared-types';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import { ButtonColor } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { UniLink } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/UniLink';
import { DropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { useSubtenants } from '@lmt/unicorn-shared/app/features/subtenants/selectors';
import { useQueryParamsInTab } from '@lmt/unicorn-shared/app/helpers/use-query-params-in-tab';
import PlusIcon from '@lmt/unicorn-shared/app/icons/plus-blue.svg?react';
import EditIcon from '@lmt/unicorn-shared/app/icons/edit.svg?react';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  NumberParam,
  useQueryParam,
  withDefault,
} from 'react-router-url-params';
import { MailSettings } from './components/mail/MailSettings';
import styles from './components/SubtenantSettingsPage.module.scss';
import { useEventCallback } from '@mui/material';
import { getTabId } from '../../features/navigation/operations';
import { useLocation } from 'react-router-dom';
import { useUniDispatch } from '../../store/types';
import { NavigationActions } from '../../features/navigation/reducer';
import { UnsavedChangesContext } from './components/types';
import { useModal } from '../common/ui-kit/modal/useModal';

export interface ISubtenantSettingsBlockProps {
  subtenantId: number | undefined;
}

export const SubtenantSettingsPage: FC = React.memo(
  function SubtenantSettingsPage() {
    const subtenants: SubtenantDto[] = useSubtenants();
    const modals = useModal();
    const i18n = useTranslation();
    const location = useLocation();
    const tabId = getTabId(location);
    const dispatch = useUniDispatch();
    const [queryParams, setQueryParams] = useQueryParamsInTab({
      subtenantId: withDefault(NumberParam, undefined),
    });

    const [unsavedChanges, setUnsavedChanges] = useState<{
      [key: string]: boolean;
    }>({});
    const changeUnsavedChanges = useCallback((key: string, value: boolean) => {
      setUnsavedChanges((prevValues) => {
        return { ...prevValues, [key]: value };
      });
    }, []);

    const pageHasUnsavedChanges = useMemo(() => {
      return Object.values(unsavedChanges).includes(true);
    }, [unsavedChanges]);

    useEffect(() => {
      setTabHasUnsavedChanges(pageHasUnsavedChanges);
    }, [pageHasUnsavedChanges]);

    const setTabHasUnsavedChanges = useEventCallback((hasChanges: boolean) => {
      if (tabId) {
        dispatch(
          NavigationActions.tabHasUnsavedChangesChanged({
            id: tabId,
            hasUnsavedChanges: hasChanges,
          }),
        );
      }
    });

    const onSubtenantChange = useEventCallback(
      async (subtenant: SubtenantDto) => {
        if (pageHasUnsavedChanges) {
          const isConfirmed = await modals.showConfirm({
            title: i18n.t(
              'subtenant_settings.unsaved_changes_confirmation.title',
            ),
            text: i18n.t(
              'subtenant_settings.unsaved_changes_confirmation.text',
            ),
            okButtonText: i18n.t(
              'subtenant_settings.unsaved_changes_confirmation.ok_text',
            ),
            cancelButtonText: i18n.t(
              'subtenant_settings.unsaved_changes_confirmation.cancel_text',
            ),
          });
          if (isConfirmed) {
            setQueryParams({ subtenantId: subtenant.id });
          }
        } else {
          setQueryParams({ subtenantId: subtenant.id });
        }
      },
    );

    useEffect(() => {
      if (subtenants && subtenants.length > 0 && !queryParams) {
        setQueryParams({ subtenantId: subtenants[0].id });
        return;
      }
      const subtenant = subtenants.find(
        (s) => s.id === queryParams.subtenantId,
      );
      if (!subtenant) {
        setQueryParams({ subtenantId: subtenants[0].id });
      }
    }, [queryParams, subtenants]);

    const forms = useGlobalForms();

    const openCreate = useEventCallback(() => forms.openSubtenantCreating());

    const openEdit = useEventCallback(
      () =>
        !!queryParams?.subtenantId &&
        forms.openSubtenantEditing(queryParams.subtenantId),
    );

    return (
      <Loading loading={!subtenants || queryParams == null}>
        <div
          className={styles.page}
          data-test-id="subtenant-settings-container"
        >
          <div className={styles.header}>
            <div className={styles.headerBlock}>
              <span className={styles.title}>
                {i18n.t('tabs.subtenant_settings.title')}
              </span>
              <DropDownInput
                useIdFunctionAsValue={true}
                required
                variant="normal"
                getOptionLabel="name"
                idFunction="id"
                options={subtenants}
                value={queryParams.subtenantId}
                onValueChanged={onSubtenantChange}
                testId="subtenant-dropdown"
              />
              <UniLink
                testId="plus-button"
                title={i18n.t('subtenants.list.edit_button')}
                color={ButtonColor.Primary}
                onClick={openEdit}
                icon={<EditIcon />}
              />
            </div>
            <div className={styles.headerBlock}>
              <UniLink
                testId="plus-button"
                title={i18n.t('subtenants.list.add_button')}
                color={ButtonColor.Primary}
                onClick={openCreate}
                icon={<PlusIcon />}
              />
            </div>
          </div>
          <div
            className={styles.settingsContainer}
            key={queryParams.subtenantId}
          >
            <UnsavedChangesContext.Provider
              value={{
                setUnsavedChanges: changeUnsavedChanges,
              }}
            >
              {!!queryParams.subtenantId && (
                <MailSettings subtenantId={queryParams.subtenantId} />
              )}
            </UnsavedChangesContext.Provider>
          </div>
        </div>
      </Loading>
    );
  },
);
